import { Settings } from '@streamr/tv-engine';
import { toUserId } from '@streamr/utils';
import { Mutex } from 'async-mutex';
import { proxy } from 'comlink';
import { EventEmitter } from 'eventemitter3';
import { MediaPacketDefragmenter, } from '../mediaplayer/MediaPacketDefragmenter';
import { MediaPacketizer } from '../mediaproducer/MediaPacketizer';
import { AudioEngine } from './AudioEngine';
import { createVideoEngine } from './createVideoEngine';
import { OptimalDelayMapper } from './OptimalDelayMapper';
import { encodedAudioFrameToAudioFrame, streamrAudioFrameToInboundEncodedAudioFrame, } from './types/EncodedAudioFrame';
import { encodedVideoFrameToVideoFrame, streamrVideoFrameToInboundEncodedVideoFrame, } from './types/EncodedVideoFrame';
import { createUserSessionId } from './types/UserSessionId';
export class MediaManager extends EventEmitter {
    videoEngine;
    audioEngine;
    mediaPacketDefragmenter;
    optimalDelayMapper;
    audioMuted = true;
    videoProperties;
    mediaPacketizer;
    // Track packet arrival times per track
    trackArrivalTimes = new Map();
    trackFrameCounts = new Map();
    lastLogTime = 0;
    audioContext;
    lastOutputtedVideoFrameNumber = 0n;
    lastOutputtedAudioFrameNumber = 0n;
    mediaPacketMutex = new Mutex();
    initCompleted = false;
    canvas;
    // Track the current video track to properly clean up when a new one is added
    // This is needed for Safari where video muting requires recreating the stream
    currentVideoTrack;
    constructor() {
        super();
        this.optimalDelayMapper = new OptimalDelayMapper();
        this.audioContext = new AudioContext({
            sampleRate: Settings.audioSampleRate,
        });
        this.videoEngine = createVideoEngine();
        this.audioEngine = new AudioEngine(this.audioContext);
        this.mediaPacketDefragmenter = new MediaPacketDefragmenter();
        this.mediaPacketDefragmenter.on('mediaPacket', async (packet) => {
            await this.mediaPacketMutex.runExclusive(async () => {
                const now = performance.now();
                // Track arrival times for each video track
                for (const videoFrame of packet.videoFrames) {
                    const trackId = videoFrame.trackId || 'unknown';
                    // Initialize tracking for this track if needed
                    if (!this.trackArrivalTimes.has(trackId)) {
                        this.trackArrivalTimes.set(trackId, []);
                        this.trackFrameCounts.set(trackId, 0);
                        //console.log(
                        //    `📊 Started tracking packets for track ${trackId.slice(0, 8)}`,
                        //);
                    }
                    // Record arrival time
                    const arrivalTimes = this.trackArrivalTimes.get(trackId);
                    if (arrivalTimes) {
                        arrivalTimes.push(now);
                        // Keep only last 100 arrival times per track
                        if (arrivalTimes.length > 100) {
                            arrivalTimes.shift();
                        }
                    }
                    // Increment frame count
                    const count = this.trackFrameCounts.get(trackId);
                    if (count !== undefined) {
                        this.trackFrameCounts.set(trackId, count + 1);
                    }
                }
                // Log statistics every 2 seconds
                if (now - this.lastLogTime > 2000) {
                    this.lastLogTime = now;
                    //this.logTrackStatistics();
                }
                // Feed frames to consumer pipeline
                const userSessionId = createUserSessionId(packet.userId, packet.messageChainId);
                if (packet.videoFrames.length > 0) {
                    this.optimalDelayMapper.registerTrackIfNotExists(packet.videoFrames[0].trackId, userSessionId, 'video');
                }
                if (packet.audioFrames.length > 0) {
                    this.optimalDelayMapper.registerTrackIfNotExists(packet.audioFrames[0].trackId, userSessionId, 'audio');
                }
                for (const videoFrame of packet.videoFrames) {
                    if (videoFrame.done) {
                        console.log('MediaManager::mediaPacket() done video frame received', {
                            trackId: videoFrame.trackId,
                        });
                        this.optimalDelayMapper.removeTrack(videoFrame.trackId);
                    }
                    const inboundVideoFrame = streamrVideoFrameToInboundEncodedVideoFrame(videoFrame, userSessionId);
                    await this.videoEngine.feedEncodedVideoFrame(inboundVideoFrame);
                }
                for (const audioFrame of packet.audioFrames) {
                    const inboundAudioFrame = streamrAudioFrameToInboundEncodedAudioFrame(audioFrame, userSessionId);
                    await this.audioEngine.feedEncodedAudioFrame(inboundAudioFrame);
                }
            });
        });
    }
    async init(streamrWrapper) {
        console.log('MediaManage::start()');
        this.emit('videoPropertiesChanged', {
            width: 1920,
            height: 1080,
            type: 'camera',
            rotation: 0,
        });
        // Update optimal delay when video player calculates it
        // The audio worklet receives updated optimal delays via feedAudioFrame messages
        await this.videoEngine.init(async (trackId, optimalDelay) => {
            this.optimalDelayMapper.updateOptimalDelay(trackId, optimalDelay);
        }, async (encodedVideoOutputTrack) => {
            await this.startReadingEncodedVideoFramesFromProducer(encodedVideoOutputTrack);
        });
        // Default audio buffer of 150ms is used while video optimal delay is being calculated
        // (requires 10 video frames, ~330ms at 30fps)
        const DEFAULT_AUDIO_BUFFER_MS = 150;
        await this.audioEngine.init((trackId) => {
            return (this.optimalDelayMapper.getOptimalDelayForSession(trackId) ?? DEFAULT_AUDIO_BUFFER_MS);
        }, async (encodedAudioOutputTrack) => {
            await this.startReadingEncodedAudioFramesFromProducer(encodedAudioOutputTrack);
        });
        this.audioEngine.referenceTimeEmitter.on('newReferenceTimes', (audioTrackId, presentationTimeMicros, realTimeMillis) => {
            // Look up video tracks in the same session as this audio track
            const videoTrackIds = this.optimalDelayMapper.getVideoTrackIdsInSameSession(audioTrackId);
            if (videoTrackIds.length > 0) {
                this.videoEngine
                    .setReferenceTimes(videoTrackIds, presentationTimeMicros, realTimeMillis)
                    .catch(console.error);
            }
        });
        // Set up video producer pipeline
        // Set up media packetizer
        this.mediaPacketizer = new MediaPacketizer();
        this.mediaPacketizer.on('mediaPacketFragment', (fragment) => {
            streamrWrapper.publishMediaPacketFragment(fragment);
        });
        streamrWrapper.on('leave', (_leave, publisherId, messageChainId) => {
            console.log('Received leave event from network');
            const userId = toUserId(publisherId);
            const userSessionId = createUserSessionId(userId, messageChainId);
            this.mediaPacketDefragmenter.removeFragmentsOfUserSession(userId, messageChainId);
            this.videoEngine.handleLeave(userSessionId).catch(console.error);
            this.audioEngine.handleLeave(userSessionId).catch(console.error);
        });
        this.initCompleted = true;
        if (this.canvas) {
            await this.videoEngine.setCanvas(this.canvas);
        }
    }
    async startReadingEncodedVideoFramesFromProducer(encodedVideoTrack) {
        const trackId = await encodedVideoTrack.getTrackId();
        encodedVideoTrack.setListener(proxy(async (encodedVideoFrame, done) => {
            if (encodedVideoFrame) {
                const videoFrame = encodedVideoFrameToVideoFrame(encodedVideoFrame);
                this.mediaPacketizer?.addVideoFrame(videoFrame, encodedVideoFrame.trackId);
                this.lastOutputtedVideoFrameNumber =
                    encodedVideoFrame.frameNumber;
            }
            if (done) {
                console.log('MediaManager::startReadingEncodedVideoFramesFromProducer() done video frame received', {
                    trackId: encodedVideoTrack.getTrackId(),
                    frameNumber: this.lastOutputtedVideoFrameNumber + 1n,
                });
                encodedVideoTrack.unsetListener();
                console.log('MediaManager::startReadingEncodedVideoFramesFromProducer() sending done video frame to media packetizer', {
                    trackId: encodedVideoTrack.getTrackId(),
                    frameNumber: this.lastOutputtedVideoFrameNumber + 1n,
                });
                this.mediaPacketizer?.sendDoneVideoFrame(trackId, this.lastOutputtedVideoFrameNumber + 1n, trackId);
                console.log('MediaManager::startReadingEncodedVideoFramesFromProducer() done video frame sent to media packetizer', {
                    trackId: encodedVideoTrack.getTrackId(),
                    frameNumber: this.lastOutputtedVideoFrameNumber + 1n,
                });
            }
        }));
    }
    async startReadingEncodedAudioFramesFromProducer(encodedAudioTrack) {
        const trackId = await encodedAudioTrack.getTrackId();
        encodedAudioTrack.setListener(proxy(async (encodedAudioFrame, done) => {
            if (encodedAudioFrame) {
                const audioFrame = encodedAudioFrameToAudioFrame(encodedAudioFrame);
                this.mediaPacketizer?.addAudioFrames([audioFrame], encodedAudioFrame.trackId);
                this.lastOutputtedAudioFrameNumber =
                    encodedAudioFrame.frameNumber;
            }
            if (done) {
                encodedAudioTrack.unsetListener();
                this.mediaPacketizer?.sendDoneAudioFrame(trackId, this.lastOutputtedAudioFrameNumber + 1n, trackId);
            }
        }));
    }
    async setCanvas(canvas) {
        this.canvas = canvas;
        if (this.initCompleted) {
            await this.videoEngine.setCanvas(canvas);
        }
    }
    isAudioMuted() {
        return this.audioMuted;
    }
    getVideoProperties() {
        return this.videoProperties;
    }
    async addVideoTrack(videoTrack) {
        console.log('MediaManager::addVideoTrack', videoTrack);
        // If no video track provided (e.g., video is muted), remove the current track
        if (!videoTrack) {
            if (this.currentVideoTrack) {
                console.log('MediaManager::addVideoTrack removing track (video muted)', {
                    trackId: this.currentVideoTrack.id,
                });
                await this.videoEngine.removeMediaStreamVideoTrack(this.currentVideoTrack);
                this.currentVideoTrack = undefined;
            }
            return;
        }
        if (videoTrack.kind !== 'video') {
            throw new Error('videoTrack is not a video track');
        }
        // If the same track is being added, skip to avoid duplicates
        if (this.currentVideoTrack?.id === videoTrack.id) {
            console.log('MediaManager::addVideoTrack skipping duplicate track', {
                trackId: videoTrack.id,
            });
            return;
        }
        // Remove the previous track before adding a new one
        // This is needed for Safari where video muting requires recreating the stream
        if (this.currentVideoTrack) {
            console.log('MediaManager::addVideoTrack removing previous track', {
                previousTrackId: this.currentVideoTrack.id,
                newTrackId: videoTrack.id,
            });
            await this.videoEngine.removeMediaStreamVideoTrack(this.currentVideoTrack);
        }
        this.currentVideoTrack = videoTrack;
        console.log('MediaManager::addVideoTrack', videoTrack);
        await this.videoEngine.addMediaStreamVideoTrack(videoTrack);
    }
    async addAudioTrack(audioTrack) {
        console.log('MediaManager::addAudioTrack', audioTrack);
        if (!audioTrack) {
            return;
        }
        if (audioTrack && audioTrack.kind !== 'audio') {
            throw new Error('audioTrack is not a audio track');
        }
        console.log('MediaManager::addAudioTrack', audioTrack);
        await this.audioEngine.addMediaStreamAudioTrack(audioTrack);
    }
    logTrackStatistics() {
        console.log('📊 Packet Arrival Statistics:');
        for (const [trackId, arrivalTimes] of this.trackArrivalTimes) {
            if (arrivalTimes.length < 2)
                continue;
            // Calculate intervals between consecutive packets
            const intervals = [];
            for (let i = 1; i < arrivalTimes.length; i++) {
                intervals.push(arrivalTimes[i] - arrivalTimes[i - 1]);
            }
            // Calculate statistics
            const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
            const minInterval = Math.min(...intervals);
            const maxInterval = Math.max(...intervals);
            const frameCount = this.trackFrameCounts.get(trackId) || 0;
            console.log(`  Track ${trackId.slice(0, 8)}: ${frameCount} frames, avg interval: ${avgInterval.toFixed(1)}ms, range: ${minInterval.toFixed(0)}-${maxInterval.toFixed(0)}ms`);
        }
    }
    async stop() {
        await this.videoEngine.destroy();
        await this.audioEngine.destroy();
    }
    async unmuteAudio() {
        console.log('MediaManager::unmuteAudio() called');
        await this.audioContext.resume();
        this.audioMuted = false;
        this.emit('muteChange', false);
    }
    async muteAudio() {
        console.log('MediaManager::muteAudio() called');
        await this.audioContext.suspend();
        this.audioMuted = true;
        this.emit('muteChange', true);
    }
    feedMediaPacketFragment(packetFragment, userId, messageChainId) {
        this.mediaPacketDefragmenter.feedMediaPacketFragment({
            ...packetFragment,
            userId,
            messageChainId,
        });
    }
}
